import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { Search as SearchIcon } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { api } from "@/lib/api";
import { usePagedList, useDebouncedValue } from "@/lib/usePagedList";
import LoadMoreButton from "@/components/LoadMoreButton";
import InsightCard from "@/components/InsightCard";
import SeoHead from "@/components/SeoHead";
import PageHero from "@/components/PageHero";
import Select from "@/components/Select";
import { TOPICS } from "@/lib/topics";
import { withDistinctImages } from "@/lib/imagePicker";

const Insights = () => {
  const { t, lang } = useLang();
  const location = useLocation();
  const initialQ = useMemo(() => new URLSearchParams(location.search).get("q") || "", [location.search]);
  const [techs, setTechs] = useState([]);
  const [filters, setFilters] = useState({ category: "all", technology: "all", since: "all", q: initialQ });

  useEffect(() => {
    api.get("/insights/technologies").then((r) => setTechs(r.data || [])).catch(() => {});
  }, []);

  const dq = useDebouncedValue(filters.q);
  const { items, loading, loadingMore, hasMore, loadMore } = usePagedList("/insights", { ...filters, q: dq.trim(), language: lang });

  const allOpt = { value: "all", label: t.filters.all };
  const topicOpts = [allOpt, ...TOPICS.map((x) => ({ value: x.en, label: lang === "fr" ? x.fr : x.en }))];
  const techOpts = [allOpt, ...techs.map((v) => ({ value: v, label: v }))];
  const periodOpts = [
    { value: "all", label: t.filters.periodAll },
    { value: "today", label: t.filters.periodToday },
    { value: "week", label: t.filters.periodWeek },
    { value: "month", label: t.filters.periodMonth },
    { value: "quarter", label: t.filters.periodQuarter },
    { value: "year", label: t.filters.periodYear },
  ];

  return (
    <div data-testid="insights-page">
      <SeoHead
        lang={lang}
        path="/insights"
        title={lang === "fr" ? "Analyses Dataleo - IA, Supply Chain & gouvernance" : "Dataleo insights - AI, Supply Chain & governance"}
        description={lang === "fr"
          ? "Analyses de fond Dataleo sur l'IA en Operations, l'architecture de décision et la gouvernance des outils IA construits par les équipes Supply Chain."
          : "Long-form Dataleo analyses on Supply Chain AI, decision architecture and governance of AI-built tools by Supply Chain teams."}
      />
      <PageHero
        kicker={lang === "fr" ? "Éditorial · Analyses" : "Editorial · Insights"}
        title={t.nav.insights}
        description={lang === "fr"
          ? "Analyses de fond Dataleo sur l'IA en Operations, la gouvernance et l'architecture de décision."
          : "Long-form Dataleo analysis on Supply Chain AI, governance and decision architecture."}
        accent="yellow"
        testId="insights-hero"
      />
      <div className="dl-container py-10">

      <div className="flex flex-wrap items-end gap-4 mb-8">
        <div className="flex flex-col gap-1 text-[12px] flex-1 min-w-[220px]">
          <span className="dl-overline">{t.filters.search}</span>
          <div className="flex items-center border border-[#E5E7EB] bg-white">
            <SearchIcon size={16} className="ml-3 text-[#4B5563]" />
            <input
              data-testid="filter-search"
              type="text"
              placeholder={t.filters.search}
              value={filters.q}
              onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              className="flex-1 px-2 py-2 text-sm bg-transparent outline-none"
            />
          </div>
        </div>
        <Select testid="insight-filter-topic" label={t.filters.category} value={filters.category} onChange={(v) => setFilters({ ...filters, category: v })} options={topicOpts} />
        <Select testid="insight-filter-tech" label={lang === "fr" ? "Technologie" : "Technology"} value={filters.technology} onChange={(v) => setFilters({ ...filters, technology: v })} options={techOpts} />
        <Select testid="filter-period" label={t.filters.period} value={filters.since} onChange={(v) => setFilters({ ...filters, since: v })} options={periodOpts} />
        <button
          data-testid="filter-reset"
          onClick={() => setFilters({ category: "all", technology: "all", since: "all", q: "" })}
          className="text-sm underline-offset-4 hover:underline text-[#4B5563]"
        >
          {t.filters.reset}
        </button>
      </div>

      {!loading && items.length === 0 ? (
        <div data-testid="insights-empty" className="text-[#4B5563] py-12 text-center">
          {lang === "fr" ? "Aucun résultat." : "No results."}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-6" data-testid="insights-grid">
          {withDistinctImages(items, "insight").map((it, i) => <InsightCard key={it.id} item={it} idx={i} />)}
        </div>
      )}
      {!loading && <LoadMoreButton hasMore={hasMore} loading={loadingMore} onClick={loadMore} lang={lang} testid="insights-load-more" />}
      </div>
    </div>
  );
};

export default Insights;
